<template>
  <div class="qa-compare">
    <div class="qa-compare__toolbar">
      <strong>{{ title }}</strong>
      <a-button
        size="small"
        @click="openFullscreen">
        На весь экран
      </a-button>
    </div>

    <div class="qa-compare__grid">
      <section class="qa-compare__panel">
        <div class="qa-compare__panel-head">
          <span>Исходник</span>
          <div class="qa-compare__controls">
            <a-button
              size="small"
              :disabled="sourceFloorIndex >= sourceImages.length - 1"
              @click="moveSourceFloor(1)">
              <up-outlined />
            </a-button>
            <span>{{ sourceFloorLabel }}</span>
            <a-button
              size="small"
              :disabled="sourceFloorIndex <= 0"
              @click="moveSourceFloor(-1)">
              <down-outlined />
            </a-button>
          </div>
        </div>
        <div class="qa-compare__image-box">
          <img
            v-if="currentSource"
            :src="currentSource"
            alt="Исходник">
          <a-empty
            v-else
            description="Нет исходника" />
        </div>
      </section>

      <section class="qa-compare__panel">
        <div class="qa-compare__panel-head">
          <span>Результат</span>
          <div class="qa-compare__controls">
            <a-select
              v-if="showResultType"
              v-model:value="resultType"
              size="small"
              :options="resultTypeOptions"
              class="qa-compare__type" />
            <a-button
              size="small"
              :disabled="resultFloorIndex >= resultImages.length - 1"
              @click="moveResultFloor(1)">
              <up-outlined />
            </a-button>
            <span>{{ resultFloorLabel }}</span>
            <a-button
              size="small"
              :disabled="resultFloorIndex <= 0"
              @click="moveResultFloor(-1)">
              <down-outlined />
            </a-button>
          </div>
        </div>
        <div class="qa-compare__image-box">
          <img
            v-if="currentResult"
            :src="currentResult"
            alt="Результат">
          <a-empty
            v-else
            description="Нет результата" />
        </div>
      </section>
    </div>

    <a-modal
      v-model:open="fullscreenOpen"
      :destroy-on-close="true"
      :footer="null"
      title="Сравнение"
      centered
      :style="{ width: '100%', top: '8px' }"
      @cancel="closeFullscreen">
      <div class="qa-compare-modal">
        <div class="qa-compare-modal__bar">
          <div
            v-if="annotationMode"
            class="qa-compare-modal__tools">
            <a-radio-group
              v-model:value="tool"
              size="small"
              class="qa-compare-modal__drawing-tools"
              button-style="solid">
              <a-tooltip title="Выделение: двигать, менять размер, поворачивать">
                <a-radio-button
                  value="select"
                  aria-label="Выделение">
                  <select-outlined />
                </a-radio-button>
              </a-tooltip>
              <a-tooltip title="Карандаш">
                <a-radio-button
                  value="pen"
                  aria-label="Карандаш">
                  <highlight-outlined />
                </a-radio-button>
              </a-tooltip>
              <a-tooltip title="Прямоугольник">
                <a-radio-button
                  value="rect"
                  aria-label="Прямоугольник">
                  <border-outlined />
                </a-radio-button>
              </a-tooltip>
              <a-tooltip title="Овал">
                <a-radio-button
                  value="ellipse"
                  aria-label="Овал">
                  <svg
                    class="qa-compare-modal__tool-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true">
                    <ellipse
                      cx="8"
                      cy="8"
                      rx="6.5"
                      ry="5"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.4" />
                  </svg>
                </a-radio-button>
              </a-tooltip>
              <a-tooltip title="Линия">
                <a-radio-button
                  value="line"
                  aria-label="Линия">
                  <svg
                    class="qa-compare-modal__tool-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true">
                    <line
                      x1="2.5"
                      y1="13.5"
                      x2="13.5"
                      y2="2.5"
                      stroke="currentColor"
                      stroke-width="1.4"
                      stroke-linecap="round" />
                  </svg>
                </a-radio-button>
              </a-tooltip>
              <a-tooltip title="Стрелка">
                <a-radio-button
                  value="arrow"
                  aria-label="Стрелка">
                  <arrow-right-outlined />
                </a-radio-button>
              </a-tooltip>
            </a-radio-group>

            <div class="qa-compare-modal__style">
              <a-tooltip title="Выбрать цвет">
                <label class="qa-compare-modal__picker">
                  <input
                    v-model="color"
                    type="color"
                    aria-label="Выбрать цвет">
                </label>
              </a-tooltip>

              <a-select
                v-model:value="lineWidth"
                size="small"
                class="qa-compare-modal__width-select">
                <a-select-option
                  v-for="width in LINE_WIDTHS"
                  :key="width"
                  :value="width">
                  <span class="qa-compare-modal__width-option">
                    <span
                      class="qa-compare-modal__width-sample"
                      :style="{ height: `${width}px`, background: color }" />
                    {{ width }} px
                  </span>
                </a-select-option>
              </a-select>
            </div>

            <a-tooltip title="Удалить выделенную фигуру (Delete)">
              <a-button
                size="small"
                :disabled="!editor.selected"
                aria-label="Удалить выделенную фигуру"
                @click="removeSelected">
                <scissor-outlined />
              </a-button>
            </a-tooltip>
            <a-tooltip title="Отменить">
              <a-button
                size="small"
                :disabled="editor.isEmpty"
                aria-label="Отменить"
                @click="undo">
                <undo-outlined />
              </a-button>
            </a-tooltip>
            <a-tooltip title="Очистить">
              <a-button
                size="small"
                :disabled="editor.isEmpty"
                aria-label="Очистить"
                @click="clear">
                <delete-outlined />
              </a-button>
            </a-tooltip>
          </div>

          <div class="qa-compare-modal__zoom">
            <a-tooltip title="Отдалить">
              <a-button
                size="small"
                :disabled="view.scale <= MIN_SCALE"
                aria-label="Отдалить"
                @click="zoomByStep(-1)">
                <minus-outlined />
              </a-button>
            </a-tooltip>
            <span class="qa-compare-modal__zoom-value">{{ zoomLabel }}</span>
            <a-tooltip title="Приблизить">
              <a-button
                size="small"
                :disabled="view.scale >= MAX_SCALE"
                aria-label="Приблизить"
                @click="zoomByStep(1)">
                <plus-outlined />
              </a-button>
            </a-tooltip>
            <a-button
              size="small"
              :disabled="isDefaultView"
              @click="resetView">
              Сбросить
            </a-button>
          </div>

          <a-button
            v-if="annotationEnabled && !annotationMode"
            type="primary"
            @click="openAnnotation()">
            Добавить замечание
          </a-button>
          <a-button
            v-if="annotationMode"
            type="primary"
            @click="saveAnnotation">
            Сохранить
          </a-button>
        </div>

        <div
          ref="canvasWrap"
          class="qa-compare-canvas-wrap">
          <canvas
            ref="canvas"
            class="qa-compare-canvas"
            :class="{ 'qa-compare-canvas--grab': isPanTool }"
            :style="annotationMode ? { cursor } : null"
            @contextmenu.prevent
            @wheel.prevent="onWheel"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointerleave="onPointerUp" />
        </div>

        <p class="qa-compare-modal__hint">
          Колесо мыши — масштаб, перетаскивание правой или средней кнопкой — сдвиг.
          <template v-if="annotationMode">
            Клик по фигуре выделяет её: углы меняют размер, верхняя ручка поворачивает,
            Delete удаляет.
          </template>
        </p>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import {
  computed, nextTick, ref, shallowReactive, watch, onBeforeUnmount,
} from 'vue';
import {
  ArrowRightOutlined,
  BorderOutlined,
  DeleteOutlined,
  DownOutlined,
  HighlightOutlined,
  MinusOutlined,
  PlusOutlined,
  ScissorOutlined,
  SelectOutlined,
  UndoOutlined,
  UpOutlined,
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import {
  COLOR_PRESETS,
  LINE_WIDTHS,
  drawSelection,
  drawShape,
} from '@/Components/CRM/QualityReview/qaShapes';
import { ShapeEditor } from '@/Components/CRM/QualityReview/qaShapeEditor';

const props = defineProps({
  sourceImages: { type: Array, default: () => [] },
  links: { type: Object, default: () => ({}) },
  annotationEnabled: { type: Boolean, default: false },
  title: { type: String, default: 'Сравнение 2D' },
  showResultType: { type: Boolean, default: true },
  // Когда видом результата управляет родитель (полоса этапов), значение приходит сюда.
  resultType: { type: String, default: null },
});

const emit = defineEmits(['annotated', 'result-type-change']);

const canvas = ref(null);
const canvasWrap = ref(null);
const fullscreenOpen = ref(false);
const annotationMode = ref(false);
const annotationKind = ref('error');
const sourceFloorIndex = ref(0);
const resultFloorIndex = ref(0);
const resultType = ref(props.resultType ?? 'jpg');

watch(() => props.resultType, (value) => {
  if (value && value !== resultType.value) resultType.value = value;
});
const tool = ref('pen');
const color = ref(COLOR_PRESETS[0]);
const lineWidth = ref(4);
// Мелкая реактивность: шаблону хватает ссылок на массив фигур и выделение,
// а перерисовку холста мы всё равно зовём руками после каждого события указателя.
const editor = shallowReactive(new ShapeEditor());
// Курсор подсказывает, что сделает нажатие: нарисовать, подвинуть, растянуть, повернуть.
const cursor = ref('crosshair');

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const SCALE_STEP = 1.25;
const IDENTITY_VIEW = { scale: 1, x: 0, y: 0 };

// Масштаб и сдвиг полноэкранного сравнения. Фигуры хранятся в координатах холста
// до применения этого преобразования, поэтому разметка не съезжает при зуме.
const view = ref({ ...IDENTITY_VIEW });
const panFrom = ref(null);

// Вне режима разметки перетаскивание всегда двигает картинку;
// в режиме разметки для этого есть правая и средняя кнопки мыши.
const isPanTool = computed(() => !annotationMode.value);
const isDefaultView = computed(() => view.value.scale === 1 && view.value.x === 0 && view.value.y === 0);
const zoomLabel = computed(() => `${Math.round(view.value.scale * 100)}%`);

const sourceImages = computed(() => props.sourceImages ?? []);
const resultTypeOptions = computed(() => [
  { value: 'jpg', label: 'JPG', disabled: !(props.links?.jpg ?? []).length },
  { value: 'svg', label: 'SVG', disabled: !(props.links?.svg ?? []).length },
]);
const resultImages = computed(() => props.links?.[resultType.value] ?? []);
const currentSource = computed(() => sourceImages.value[sourceFloorIndex.value] ?? null);
const currentResult = computed(() => resultImages.value[resultFloorIndex.value] ?? null);
const sourceFloorLabel = computed(() => (
  sourceImages.value.length ? `${sourceFloorIndex.value + 1} / ${sourceImages.value.length}` : '0 / 0'
));
const resultFloorLabel = computed(() => (
  resultImages.value.length ? `${resultFloorIndex.value + 1} / ${resultImages.value.length}` : '0 / 0'
));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Кэш нужен, потому что перерисовка идёт на каждое движение указателя.
const imageCache = new Map();

const loadImage = (src) => {
  if (!src) return Promise.resolve(null);
  if (imageCache.has(src)) return imageCache.get(src);

  const promise = new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

const drawContain = (ctx, image, box) => {
  ctx.save();
  ctx.fillStyle = '#fafafa';
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.strokeStyle = '#e8e8e8';
  ctx.strokeRect(box.x, box.y, box.w, box.h);

  if (image) {
    const scale = Math.min(box.w / image.naturalWidth, box.h / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    const x = box.x + (box.w - width) / 2;
    const y = box.y + (box.h - height) / 2;
    ctx.drawImage(image, x, y, width, height);
  } else {
    ctx.fillStyle = '#999';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Нет изображения', box.x + box.w / 2, box.y + box.h / 2);
  }
  ctx.restore();
};

const drawComparison = async (
  targetCanvas,
  {
    includeAnnotations = true, includeSelection = false, exportSize = null, viewport = null,
  } = {},
) => {
  const source = await loadImage(currentSource.value);
  const result = await loadImage(currentResult.value);
  const width = exportSize?.width ?? Math.max(900, canvasWrap.value?.clientWidth ?? 900);
  const height = exportSize?.height ?? Math.max(560, Math.min(window.innerHeight - 180, 760));
  const ratio = window.devicePixelRatio || 1;
  const currentView = viewport ?? view.value;

  targetCanvas.width = Math.round(width * ratio);
  targetCanvas.height = Math.round(height * ratio);
  targetCanvas.style.width = `${width}px`;
  targetCanvas.style.height = `${height}px`;

  const ctx = targetCanvas.getContext('2d');
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);

  ctx.translate(currentView.x, currentView.y);
  ctx.scale(currentView.scale, currentView.scale);

  ctx.fillStyle = '#262626';
  ctx.font = '600 15px sans-serif';
  ctx.fillText(`Исходник: ${sourceFloorIndex.value + 1} этаж`, 16, 24);
  ctx.fillText(`${resultType.value.toUpperCase()} результат: ${resultFloorIndex.value + 1} этаж`, width / 2 + 8, 24);

  const gap = 16;
  const top = 40;
  const boxHeight = height - top - 16;
  const boxWidth = (width - gap - 32) / 2;
  drawContain(ctx, source, {
    x: 16, y: top, w: boxWidth, h: boxHeight,
  });
  drawContain(ctx, result, {
    x: 16 + boxWidth + gap, y: top, w: boxWidth, h: boxHeight,
  });

  if (includeAnnotations) {
    editor.visibleShapes.forEach((shape) => drawShape(ctx, shape));
    // Рамку выделения рисуем только на экране: в скриншот замечания она не попадает.
    if (includeSelection && editor.selected) drawSelection(ctx, editor.selected, currentView.scale);
  }
};

async function renderCanvas() {
  if (!fullscreenOpen.value || !canvas.value) return;
  try {
    await drawComparison(canvas.value, { includeSelection: true });
  } catch (e) {
    message.error('Не удалось отрисовать сравнение');
  }
}

const moveSourceFloor = (delta) => {
  sourceFloorIndex.value = clamp(sourceFloorIndex.value + delta, 0, Math.max(sourceImages.value.length - 1, 0));
  renderCanvas();
};

const moveResultFloor = (delta) => {
  resultFloorIndex.value = clamp(resultFloorIndex.value + delta, 0, Math.max(resultImages.value.length - 1, 0));
  renderCanvas();
};

watch(resultType, (value) => {
  resultFloorIndex.value = clamp(resultFloorIndex.value, 0, Math.max(resultImages.value.length - 1, 0));
  emit('result-type-change', value);
  renderCanvas();
});

const setResultType = (value) => {
  if (value === resultType.value) return;
  resultType.value = value;
};

// Ссылки приезжают после загрузки планировки: если у выбранного вида картинок нет,
// переключаемся на тот, который реально есть.
watch(() => props.links, () => {
  if (resultImages.value.length) return;

  const fallback = resultTypeOptions.value.find((option) => !option.disabled);
  if (fallback) setResultType(fallback.value);
}, { deep: true, immediate: true });

const openFullscreen = async () => {
  annotationMode.value = false;
  fullscreenOpen.value = true;
  view.value = { ...IDENTITY_VIEW };
  await nextTick();
  await renderCanvas();
};

const openAnnotation = async (kind = 'error') => {
  if (!props.annotationEnabled) return;
  if (!currentSource.value && !currentResult.value) {
    message.warning('Нет изображений для создания замечания');
    return;
  }
  annotationKind.value = kind;
  annotationMode.value = true;
  tool.value = 'pen';
  editor.reset();
  view.value = { ...IDENTITY_VIEW };
  fullscreenOpen.value = true;
  await nextTick();
  await renderCanvas();
};

const closeFullscreen = () => {
  fullscreenOpen.value = false;
  annotationMode.value = false;
  editor.deselect();
  panFrom.value = null;
  view.value = { ...IDENTITY_VIEW };
};

// Координаты указателя в системе холста до масштабирования.
const canvasPosition = (event) => {
  const rect = canvas.value.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;
  return {
    x: ((event.clientX - rect.left) * (canvas.value.width / rect.width)) / ratio,
    y: ((event.clientY - rect.top) * (canvas.value.height / rect.height)) / ratio,
  };
};

// Те же координаты, но с учётом текущего зума — в них живут фигуры.
const pointerPosition = (event) => {
  const point = canvasPosition(event);
  const { scale, x, y } = view.value;
  return { x: (point.x - x) / scale, y: (point.y - y) / scale };
};

const applyZoom = (nextScale, anchor) => {
  const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
  if (scale === view.value.scale) return;

  const { scale: prevScale, x, y } = view.value;
  const factor = scale / prevScale;

  view.value = scale === MIN_SCALE
    ? { ...IDENTITY_VIEW }
    : {
      scale,
      // точка под курсором остаётся на месте
      x: anchor.x - (anchor.x - x) * factor,
      y: anchor.y - (anchor.y - y) * factor,
    };

  renderCanvas();
};

const zoomByStep = (direction) => {
  const anchor = {
    x: (canvas.value?.clientWidth ?? 0) / 2,
    y: (canvas.value?.clientHeight ?? 0) / 2,
  };
  applyZoom(view.value.scale * (direction > 0 ? SCALE_STEP : 1 / SCALE_STEP), anchor);
};

const onWheel = (event) => {
  if (!fullscreenOpen.value) return;
  applyZoom(view.value.scale * (event.deltaY < 0 ? SCALE_STEP : 1 / SCALE_STEP), canvasPosition(event));
};

const resetView = () => {
  view.value = { ...IDENTITY_VIEW };
  renderCanvas();
};

const startPan = (event) => {
  const point = canvasPosition(event);
  panFrom.value = { x: point.x - view.value.x, y: point.y - view.value.y };
};

// Разметку правят на зумленном холсте, а допуск попадания должен остаться экранным.
const hitTolerance = () => 6 / (view.value.scale || 1);

const onPointerDown = (event) => {
  canvas.value.setPointerCapture(event.pointerId);

  // Правая и средняя кнопки двигают картинку, левая рисует.
  if (event.button === 1 || event.button === 2 || isPanTool.value) {
    startPan(event);
    return;
  }

  if (!annotationMode.value) return;

  editor.pointerDown(pointerPosition(event), {
    tool: tool.value,
    color: color.value,
    width: lineWidth.value,
    tolerance: hitTolerance(),
  });
  renderCanvas();
};

const onPointerMove = (event) => {
  if (panFrom.value) {
    const point = canvasPosition(event);
    view.value = {
      ...view.value,
      x: point.x - panFrom.value.x,
      y: point.y - panFrom.value.y,
    };
    renderCanvas();
    return;
  }

  if (!annotationMode.value) return;

  const point = pointerPosition(event);
  cursor.value = editor.cursorAt(point, { tool: tool.value, tolerance: hitTolerance() });

  if (editor.pointerMove(point)) renderCanvas();
};

const onPointerUp = () => {
  panFrom.value = null;
  if (editor.pointerUp()) renderCanvas();
};

const undo = () => {
  editor.undo();
  renderCanvas();
};

const clear = () => {
  editor.reset();
  renderCanvas();
};

const removeSelected = () => {
  if (editor.deleteSelected()) renderCanvas();
};

// Backspace в поле ввода — это правка значения, а не удаление фигуры.
const isTypingTarget = (target) => ['INPUT', 'SELECT', 'TEXTAREA'].includes(target?.tagName)
  || Boolean(target?.isContentEditable);

const onKeyDown = (event) => {
  if (!fullscreenOpen.value || !annotationMode.value) return;

  if (event.key === 'Delete' || event.key === 'Backspace') {
    if (!isTypingTarget(event.target)) removeSelected();
    return;
  }

  // Esc сначала снимает выделение, закрытие окна остаётся на второй раз.
  if (event.key === 'Escape' && editor.deselect()) {
    event.stopPropagation();
    renderCanvas();
  }
};

const captureSnapshot = async () => {
  const exportCanvas = document.createElement('canvas');
  await drawComparison(exportCanvas, {
    includeAnnotations: false,
    exportSize: { width: 1600, height: 900 },
    viewport: IDENTITY_VIEW,
  });
  return exportCanvas.toDataURL('image/png');
};

const saveAnnotation = async () => {
  try {
    // Маркеры выделения не должны попасть в скриншот замечания.
    editor.deselect();
    await renderCanvas();
    emit('annotated', {
      kind: annotationKind.value,
      screenshot: canvas.value.toDataURL('image/png'),
      // этаж нужен, чтобы в замечании было видно, к какой части многоэтажной планировки оно относится
      floorIndex: resultFloorIndex.value,
      floorsTotal: Math.max(resultImages.value.length, sourceImages.value.length),
    });
    annotationMode.value = false;
  } catch (e) {
    message.error('Не удалось сохранить скриншот');
  }
};

const onResize = () => renderCanvas();
window.addEventListener('resize', onResize);
window.addEventListener('keydown', onKeyDown, true);
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('keydown', onKeyDown, true);
});

defineExpose({
  openAnnotation, captureSnapshot, openFullscreen, setResultType,
});
</script>

<style scoped>
.qa-compare {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.qa-compare__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.qa-compare__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  min-height: 0;
}
.qa-compare__panel {
  min-width: 0;
}
.qa-compare__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-weight: 600;
}
.qa-compare__controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.65);
}
.qa-compare__type {
  width: 76px;
}
.qa-compare__image-box {
  height: min(62vh, 560px);
  min-height: 320px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.qa-compare__image-box img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.qa-compare-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.qa-compare-modal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.qa-compare-modal__tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.qa-compare-modal__drawing-tools :deep(.ant-radio-button-wrapper) {
  width: 34px;
  padding-inline: 0;
  text-align: center;
}
.qa-compare-modal__tool-icon {
  width: 1em;
  height: 1em;
  vertical-align: -0.125em;
}
.qa-compare-canvas-wrap {
  width: 100%;
  overflow: auto;
  text-align: center;
  background: #f5f5f5;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
}
.qa-compare-canvas {
  display: inline-block;
  max-width: 100%;
  background: #fff;
  cursor: crosshair;
  touch-action: none;
}
.qa-compare-canvas--grab {
  cursor: grab;
}
.qa-compare-canvas--grab:active {
  cursor: grabbing;
}
.qa-compare-modal__style {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.qa-compare-modal__color {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}
.qa-compare-modal__color--active {
  border-color: #1677ff;
}
.qa-compare-modal__picker {
  display: inline-flex;
  cursor: pointer;
}
.qa-compare-modal__picker input {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: none;
  cursor: pointer;
}
.qa-compare-modal__width-select {
  width: 110px;
}
.qa-compare-modal__width-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  line-height: 22px;
}
.qa-compare-modal__width-sample {
  flex: 0 0 auto;
  align-self: center;
  width: 32px;
  border-radius: 2px;
}
.qa-compare-modal__zoom {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.qa-compare-modal__zoom-value {
  min-width: 48px;
  text-align: center;
  color: rgba(0, 0, 0, 0.65);
}
.qa-compare-modal__hint {
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}
@media (max-width: 900px) {
  .qa-compare__grid {
    grid-template-columns: 1fr;
  }
}
</style>
